/**
 * AutoHeartbeatManager
 * Manages automatic periodic heartbeat to keep presence fresh
 * Matches the test app's auto-heartbeat implementation
 */
import { ConnectionState } from '../shared/types';
export class AutoHeartbeatManager {
    /**
     * Create AutoHeartbeatManager
     * @param presenceClient - PresenceClient instance
     * @param intervalSeconds - Heartbeat interval in seconds (default: 30)
     */
    constructor(presenceClient, intervalSeconds = 30) {
        this.presenceClient = presenceClient;
        this.intervalSeconds = intervalSeconds;
        this.intervalId = null;
        this.lastHeartbeat = null;
        this.isRunning = false;
        this.stateChangeUnsubscribe = null;
        this.wasPausedDueToDisconnect = false;
        // Listen for connection state changes
        this.stateChangeUnsubscribe = this.presenceClient.onStateChange((state) => {
            this.handleConnectionStateChange(state);
        });
    }
    /**
     * Start automatic heartbeat
     * Sends heartbeat immediately and then every intervalSeconds
     */
    start() {
        if (this.isRunning) {
            console.warn('[AutoHeartbeat] Already running');
            return;
        }
        console.log(`[AutoHeartbeat] Starting with ${this.intervalSeconds}s interval`);
        // Send initial heartbeat immediately
        this.sendHeartbeat();
        // Setup interval for subsequent heartbeats
        this.intervalId = setInterval(() => {
            this.sendHeartbeat();
        }, this.intervalSeconds * 1000);
        this.isRunning = true;
    }
    /**
     * Stop automatic heartbeat
     */
    stop() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
        this.isRunning = false;
        console.log('[AutoHeartbeat] Stopped');
    }
    /**
     * Send a single heartbeat
     * @private
     */
    async sendHeartbeat() {
        try {
            // Check if connected before invoking hub method
            if (!this.presenceClient.isConnected()) {
                console.warn('[AutoHeartbeat] Not connected, skipping heartbeat');
                return;
            }
            await this.presenceClient.heartbeat();
            this.lastHeartbeat = new Date();
            console.log('[AutoHeartbeat] Heartbeat sent successfully at', this.lastHeartbeat.toISOString());
        }
        catch (error) {
            console.error('[AutoHeartbeat] Failed to send heartbeat:', error);
        }
    }
    /**
     * Get the timestamp of the last successful heartbeat
     * @returns Last heartbeat date or null if never sent
     */
    getLastHeartbeat() {
        return this.lastHeartbeat;
    }
    /**
     * Update the heartbeat interval
     * If currently running, restarts with new interval
     * @param seconds - New interval in seconds
     */
    setInterval(seconds) {
        if (seconds < 10) {
            console.warn('[AutoHeartbeat] Interval too short, using minimum 10 seconds');
            seconds = 10;
        }
        if (seconds > 300) {
            console.warn('[AutoHeartbeat] Interval too long, using maximum 300 seconds');
            seconds = 300;
        }
        this.intervalSeconds = seconds;
        // If running, restart with new interval
        if (this.isRunning) {
            console.log(`[AutoHeartbeat] Updating interval to ${seconds}s`);
            this.stop();
            this.start();
        }
    }
    /**
     * Get current interval setting
     * @returns Interval in seconds
     */
    getInterval() {
        return this.intervalSeconds;
    }
    /**
     * Check if heartbeat is currently running
     * @returns True if running, false otherwise
     */
    isActive() {
        return this.isRunning;
    }
    /**
     * Handle connection state changes
     * Pauses heartbeat when disconnected/reconnecting, resumes when connected
     * @private
     */
    handleConnectionStateChange(state) {
        if (state === ConnectionState.Disconnected || state === ConnectionState.Reconnecting) {
            // Pause if running
            if (this.isRunning) {
                console.log(`[AutoHeartbeat] Pausing due to connection state: ${state}`);
                if (this.intervalId) {
                    clearInterval(this.intervalId);
                    this.intervalId = null;
                }
                this.wasPausedDueToDisconnect = true;
            }
        }
        else if (state === ConnectionState.Connected) {
            // Resume if was paused due to disconnect
            if (this.wasPausedDueToDisconnect && this.isRunning) {
                console.log('[AutoHeartbeat] Resuming after reconnection');
                // Send immediate heartbeat on reconnect
                this.sendHeartbeat();
                // Restart interval
                this.intervalId = setInterval(() => {
                    this.sendHeartbeat();
                }, this.intervalSeconds * 1000);
                this.wasPausedDueToDisconnect = false;
            }
        }
    }
    /**
     * Cleanup and destroy the heartbeat manager
     * Stops heartbeat and unsubscribes from connection state changes
     */
    destroy() {
        this.stop();
        if (this.stateChangeUnsubscribe) {
            this.stateChangeUnsubscribe();
            this.stateChangeUnsubscribe = null;
        }
    }
}
//# sourceMappingURL=AutoHeartbeatManager.js.map